import { CsvFile, CsvFileVersion, CsvFileValidationStatus } from '@/types/csvFiles';

function formatUserForCell(user: unknown): { name: string; email?: string } | undefined {
  if (!user || typeof user !== 'object') return undefined;
  const u = user as {
    first_name?: unknown;
    last_name?: unknown;
    name?: unknown;
    email?: unknown;
  };
  const firstName = typeof u.first_name === 'string' ? u.first_name : undefined;
  const lastName = typeof u.last_name === 'string' ? u.last_name : undefined;
  const name =
    firstName && lastName
      ? `${firstName} ${lastName}`
      : (typeof u.name === 'string' && u.name) ||
          (typeof u.email === 'string' && u.email) ||
          'Unknown';
  const email = typeof u.email === 'string' ? u.email : undefined;
  return { name, email };
}

/**
 * Format version number for display
 * @param versionNumber - Sequential version number (optional)
 * @param versionId - Version ID as fallback
 * @returns Formatted version string
 */
export function formatVersionNumber(
  versionNumber?: number,
  versionId?: number
): string {
  if (versionNumber !== undefined && versionNumber !== null) {
    return `v.${versionNumber}`;
  }
  if (versionId !== undefined && versionId !== null) {
    return `v-id: ${versionId}`;
  }
  return 'v.0';
}

/**
 * Get version badge text for a CSV file
 * @param file - CSV file object
 * @returns Version badge text
 */
export function getVersionBadgeText(file: CsvFile): string {
  return formatVersionNumber(file.version_number, file.id);
}

/**
 * Get version badge text for a CSV file version
 * @param version - CSV file version object
 * @returns Version badge text
 */
export function getVersionBadgeTextForVersion(
  version: CsvFileVersion
): string {
  return formatVersionNumber(version.version_number, version.id);
}

/**
 * Get last imported version info for display
 * @param file - CSV file object
 * @returns Object with version info or null
 */
export function getLastImportedVersionInfo(file: CsvFile): {
  versionNumber: string;
  importedAt?: string;
  importedBy?: { name: string; email?: string };
} | null {
  const importedVersion = file.latest_imported_version;
  if (importedVersion) {
    return {
      versionNumber: formatVersionNumber(
        importedVersion.order_index,
        importedVersion.id
      ),
      importedAt: importedVersion.imported_at || undefined,
      importedBy: importedVersion.imported_by
        ? formatUserForCell(importedVersion.imported_by)
        : undefined,
    };
  }

  // Backward-compatible fallback (in case older backend still provides these fields)
  if (file.last_imported_version) {
    return {
      versionNumber: formatVersionNumber(
        file.last_imported_version,
        file.last_version_id
      ),
      importedAt: file.last_imported_at,
      importedBy: file.last_imported_by
        ? formatUserForCell(file.last_imported_by)
        : undefined,
    };
  }

  return null;
}

/**
 * Get validation status info for display
 * @param file - CSV file or version object
 * @returns Object with status info or null
 */
export function getValidationStatusInfo(
  file: CsvFile | CsvFileVersion
): {
  status: CsvFileValidationStatus;
  changedAt?: string;
  changedBy?: { name: string; email?: string };
} | null {
  type StatusHistoryEntry = {
    status?: CsvFileValidationStatus;
    changed_at?: string;
    changed_by?: unknown;
  };

  type StatusContainer = {
    validation_status?: CsvFileValidationStatus;
    status?: CsvFileValidationStatus;
    last_version?: { status?: CsvFileValidationStatus; status_history?: unknown };
    status_history?: unknown;
    approved_at?: string;
    approved_by?: unknown;
    rejected_at?: string;
    rejected_by?: unknown;
  };

  const container = file as unknown as StatusContainer;

  const status: CsvFileValidationStatus =
    container.validation_status ||
    container.status ||
    container.last_version?.status ||
    'pending';

  if (status === 'pending') {
    return null; // No info for pending status
  }

  const historyRaw =
    container.status_history || container.last_version?.status_history || [];
  const history = Array.isArray(historyRaw) ? (historyRaw as StatusHistoryEntry[]) : [];

  if (Array.isArray(history) && history.length > 0) {
    const lastMatchingEntry = [...history]
      .reverse()
      .find((entry: StatusHistoryEntry) => entry?.status === status);

    if (lastMatchingEntry) {
      return {
        status,
        changedAt: lastMatchingEntry.changed_at,
        changedBy: formatUserForCell(lastMatchingEntry.changed_by),
      };
    }
  }

  // Backward-compatible fallback (older fields)
  if (status === 'approved') {
    const approvedAt = container.approved_at;
    const approvedBy = container.approved_by;
    if (!approvedAt && !approvedBy) return null;
    return {
      status: 'approved',
      changedAt: approvedAt,
      changedBy: approvedBy ? formatUserForCell(approvedBy) : undefined,
    };
  }

  if (status === 'rejected') {
    const rejectedAt = container.rejected_at;
    const rejectedBy = container.rejected_by;
    if (!rejectedAt && !rejectedBy) return null;
    return {
      status: 'rejected',
      changedAt: rejectedAt,
      changedBy: rejectedBy ? formatUserForCell(rejectedBy) : undefined,
    };
  }

  return null;
}

